import Cookies from 'js-cookie';

const ACCESS_TOKEN_COOKIE = 'access_token';
const REFRESH_TOKEN_COOKIE = 'refresh_token';
const ACCESS_TOKEN_EXPIRY_DAYS = 1; // Access tokens expire in 1 day
const REFRESH_TOKEN_EXPIRY_DAYS = 30; // Refresh tokens expire in 30 days

/**
 * Token Utility Functions
 * Handles storage and retrieval of access and refresh tokens in cookies
 */

/**
 * Get access token from cookies
 */
export function getAccessToken(): string | undefined {
  return Cookies.get(ACCESS_TOKEN_COOKIE);
}

/**
 * Set access token in cookies
 */
export function setAccessToken(token: string): void {
  Cookies.set(ACCESS_TOKEN_COOKIE, token, {
    expires: ACCESS_TOKEN_EXPIRY_DAYS,
    path: '/',
    secure: import.meta.env.PROD, // Only secure in production
    sameSite: 'strict'
  });
}

/**
 * Get refresh token from cookies
 */
export function getRefreshToken(): string | undefined {
  return Cookies.get(REFRESH_TOKEN_COOKIE);
}

/**
 * Set refresh token in cookies
 */
export function setRefreshToken(token: string): void {
  Cookies.set(REFRESH_TOKEN_COOKIE, token, {
    expires: REFRESH_TOKEN_EXPIRY_DAYS,
    path: '/',
    secure: import.meta.env.PROD, // Only secure in production
    sameSite: 'strict'
  });
}

/**
 * Set both access and refresh tokens
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
}

/**
 * Clear access token from cookies
 */
export function removeAccessToken(): void {
  Cookies.remove(ACCESS_TOKEN_COOKIE, { path: '/' });
}

/**
 * Clear refresh token from cookies
 */
export function removeRefreshToken(): void {
  Cookies.remove(REFRESH_TOKEN_COOKIE, { path: '/' });
}

/**
 * Clear all tokens from cookies
 */
export function clearTokens(): void {
  removeAccessToken();
  removeRefreshToken();
}

/**
 * Check if user has valid tokens
 */
export function hasValidTokens(): boolean {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  return !!(accessToken && refreshToken);
}
